import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Chat,
	FlexGap,
	Paragraph,
	QuillText,
	Stars,
	Subheading,
	TextBubble,
	tokens,
} from '@treams/treams-ui';

import VoiceMemoPlayback from '../../../packages/@treams/treams-app/src/components/voice-memos/VoiceMemoPlayback';
import { CommentsWithData } from '../../comments/Comments';
import CommentBar from '../../comments/components/CommentBar';
import CompetencyDisplay from '../../packages/kakapo-ui-application/common/containers/CompetencyDisplay';
import { feedbackQuestionLevels } from '../feedbackQuestionLevels';

const DateLabel = styled(Paragraph)`
	opacity: 0;
	transition: 250ms opacity ease-out;
`;

const Container = styled(Flex)`
	page-break-inside: avoid;

	:hover {
		${DateLabel} {
			opacity: 1;
		}
	}
`;

const Replies = styled.div`
	&:not(:empty) {
		@media print {
			padding-top: ${tokens.gap.group}px;
		}
	}
`;

type Props = {
	response: graphql.FeedbackResponse;
	recipient: graphql.FeedbackRecipient;
	question: graphql.FeedbackRequestQuestion;
	answer: graphql.FeedbackResponseAnswer;
	small?: boolean;
};

export default function FeedbackResultAnswer(props: Props) {
	const [composer, setComposer] = useState(false);
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const metadata = { questionID: props.question.id };

	const selectedOption = props.answer.selectedOptionID
		? props.question.options?.find(({ id }) => id === props.answer.selectedOptionID)
		: undefined;

	const extras = (
		<>
			{selectedOption && (
				<Box mt={2}>
					<Paragraph fontWeight="bold">{localize(selectedOption.text).text}</Paragraph>
				</Box>
			)}
			{props.question.competency && typeof props.answer.competencyLevelIndex === 'number' && (
				<Box mt={2} width="100%">
					<CompetencyDisplay
						levels={feedbackQuestionLevels(props.question)}
						manager={props.answer.competencyLevelIndex}
					/>
				</Box>
			)}
			{props.question.competency && props.answer.competencyLevelIndex === null && (
				<Box mt={2}>
					<Paragraph fontWeight="bold">{t('feedback.answer.not.observed')}</Paragraph>
				</Box>
			)}
			{props.answer.voiceMemo && (
				<Box py={2}>
					<VoiceMemoPlayback
						samples={props.answer.voiceMemo.samples}
						source={props.answer.voiceMemo.asset.source.url}
					/>
				</Box>
			)}
		</>
	);

	const comments =
		props.response.recipient.__typename === 'User' ? (
			<CommentsWithData
				empty={false}
				composer={composer}
				comments={props.response.comments}
				autoFocus
				target={props.response}
				metadata={metadata}
				onCancelComposer={() => setComposer(false)}
				onRefetch={() => {}}
			/>
		) : null;

	const commentBar = (
		<CommentBar.Container
			onComment={() => setComposer((composer) => !composer)}
			targetID={props.response.id}
			targetType={graphql.CommentTargetType.FeedbackResponse}
			metadata={metadata}
			enabled={props.response.recipient.__typename === 'User'}
		>
			{props.small ? (
				<TextBubble
					user={props.recipient}
					text={props.answer.text}
					stars={typeof props.answer.stars === 'number' ? props.answer.stars : undefined}
				>
					{extras}
				</TextBubble>
			) : (
				<Chat mb={3}>
					<Flex alignItems="center" justifyContent="space-between" mb={2} px={0}>
						<FlexGap gap={8} alignItems="center" justifyContent="flex-start" my={0}>
							<Subheading>
								{props.recipient.firstName} {props.recipient.lastName}
							</Subheading>
						</FlexGap>
						<DateLabel>{moment(props.response.createdAt).format('LL')}</DateLabel>
					</Flex>
					{props.answer.text && (
						<Box>
							<QuillText value={props.answer.text} />
						</Box>
					)}
					{typeof props.answer.stars === 'number' && (
						<Box>
							<Stars mt={2} stars={props.answer.stars} />
						</Box>
					)}
					{extras}
				</Chat>
			)}
		</CommentBar.Container>
	);

	if (props.small) {
		return (
			<Box minWidth={0} width="100%">
				{commentBar}
				<Replies>{comments}</Replies>
			</Box>
		);
	}

	return (
		<Container>
			<Avatar user={props.recipient} size={32} flexGrow={0} flexShrink={0} />
			<Box ml={2} flexGrow={1} flexShrink={1}>
				{commentBar}
				<Replies>{comments}</Replies>
			</Box>
		</Container>
	);
}

function FeedbackResultAnswerSkeleton(props: { small?: boolean }) {
	if (props.small) {
		return <TextBubble.Skeleton />;
	}

	return (
		<Container>
			<Avatar.Skeleton size={32} flexGrow={0} flexShrink={0} />
			<Chat ml={2} flexGrow={1} flexShrink={1}>
				<Subheading.Skeleton />
				<Paragraph.Skeleton mt={2} />
			</Chat>
		</Container>
	);
}

FeedbackResultAnswer.Skeleton = FeedbackResultAnswerSkeleton;
